/**
 * useStakeStepper Hook
 * Rival-first review-screen coin stake stepper (specs/challenge-flow-redesign, AC-4.3/4.4).
 * Steps `stakeCoins` by REVIEW_STAKE_STEP, clamped to [REVIEW_STAKE_MIN, balance].
 * Pure helpers are exported for unit testing.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { REVIEW_STAKE_MIN, REVIEW_STAKE_STEP } from '../constants/create.constants';

/**
 * Effective stake ceiling = the smaller of what the sender can afford and what the opponent
 * can cover. A stake is a mutual wager: you can never put in more than your rival has.
 * - `ignoreBalance` (guests) lifts the *sender* cap to unbounded, but an explicit `opponentCap`
 *   still wins — the Monkey carries 0 coins, so a coin stake against it is impossible.
 * - `opponentCap === undefined` means "no single opponent to cap against" (group challenges),
 *   so only the sender balance applies.
 */
export const effectiveStakeBalance = (
  balance: number,
  ignoreBalance: boolean = false,
  opponentCap?: number,
): number => {
  const senderBalance = ignoreBalance ? Number.POSITIVE_INFINITY : balance;
  return opponentCap === undefined ? senderBalance : Math.min(senderBalance, opponentCap);
};

/**
 * Largest stake reachable in whole `step` increments without exceeding `balance`.
 * A non-finite balance (guests, who play the Monkey for free) is treated as unbounded.
 */
export const maxStakeForBalance = (
  balance: number,
  step: number = REVIEW_STAKE_STEP,
  min: number = REVIEW_STAKE_MIN
): number => {
  if (balance === Number.POSITIVE_INFINITY) return Number.POSITIVE_INFINITY;
  const safeBalance = Number.isFinite(balance) ? Math.max(min, Math.floor(balance)) : min;
  if (safeBalance <= min) return min;
  return min + Math.floor((safeBalance - min) / step) * step;
};

/** Clamp an arbitrary stake into the valid [min, maxForBalance] range, snapped down to a step. */
export const clampStake = (
  stake: number,
  balance: number,
  step: number = REVIEW_STAKE_STEP,
  min: number = REVIEW_STAKE_MIN
): number => {
  const max = maxStakeForBalance(balance, step, min);
  if (!Number.isFinite(stake)) return min;
  if (stake <= min) return min;
  if (stake >= max) return max;
  // snap to nearest lower step boundary from min
  return min + Math.floor((stake - min) / step) * step;
};

export const nextStake = (
  stake: number,
  balance: number,
  step: number = REVIEW_STAKE_STEP,
  min: number = REVIEW_STAKE_MIN
): number => Math.min(clampStake(stake, balance, step, min) + step, maxStakeForBalance(balance, step, min));

export const prevStake = (
  stake: number,
  step: number = REVIEW_STAKE_STEP,
  min: number = REVIEW_STAKE_MIN
): number => Math.max(min, clampStake(stake, Number.POSITIVE_INFINITY, step, min) - step);

/**
 * Winner-takes pot = every accepted player's stake (server: pot = stake × acceptedCount,
 * settlementEconomy.helper). 1v1 default = stake × 2; group challenges pass the member count
 * (a "max win" — the real pot depends on how many members accept).
 */
export const winnerPot = (stake: number, participants: number = 2): number =>
  Math.max(0, Math.floor(stake)) * Math.max(2, Math.floor(participants));

export interface UseStakeStepperArgs {
  stake: number;
  balance: number;
  setStake: (next: number) => void;
  /** Guests play the Monkey for free — lifts the sender cap (but not an opponent cap). */
  ignoreBalance?: boolean;
  /**
   * The opponent's coin balance — the stake can never exceed it. `0` for the Monkey (no coins),
   * a friend's balance for a 1v1 duel, `undefined` for group challenges (no single opponent to cap).
   */
  opponentCap?: number;
  /** Players sharing the pot: 2 for a friend duel, group member count for group challenges. */
  participants?: number;
}

export interface UseStakeStepperReturn {
  stake: number;
  pot: number;
  canIncrement: boolean;
  canDecrement: boolean;
  increment: () => void;
  decrement: () => void;
}

export const useStakeStepper = ({
  stake,
  balance,
  setStake,
  ignoreBalance = false,
  opponentCap,
  participants = 2,
}: UseStakeStepperArgs): UseStakeStepperReturn => {
  const effectiveBalance = useMemo(
    () => effectiveStakeBalance(balance, ignoreBalance, opponentCap),
    [balance, ignoreBalance, opponentCap],
  );
  const max = useMemo(() => maxStakeForBalance(effectiveBalance), [effectiveBalance]);

  // A stake chosen against one rival can outlive the switch to a poorer one (e.g. set 500 vs a
  // rich friend, then back out and pick the Monkey). The stored value is the source of truth the
  // Send uses, so snap it down to the new ceiling — the buttons alone would leave a stale wager.
  const clampedStake = useMemo(() => clampStake(stake, effectiveBalance), [stake, effectiveBalance]);
  useEffect(() => {
    if (clampedStake !== stake) setStake(clampedStake);
  }, [clampedStake, stake, setStake]);

  const canIncrement = clampedStake < max;
  const canDecrement = clampedStake > REVIEW_STAKE_MIN;

  const increment = useCallback(() => {
    if (!canIncrement) return;
    setStake(nextStake(clampedStake, effectiveBalance));
  }, [canIncrement, setStake, clampedStake, effectiveBalance]);

  const decrement = useCallback(() => {
    if (!canDecrement) return;
    setStake(prevStake(clampedStake));
  }, [canDecrement, setStake, clampedStake]);

  return {
    stake: clampedStake,
    pot: winnerPot(clampedStake, participants),
    canIncrement,
    canDecrement,
    increment,
    decrement,
  };
};
